import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
  Button
} from '@mui/material';
import {
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const QuickAuditTrail = ({ entityType, entityId, entityName, maxItems = 5 }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (entityId) {
      fetchRecentAuditLogs();
    }
  }, [entityId, entityType]);

  const fetchRecentAuditLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let endpoint = '';
      if (entityType === 'charter' || entityType === 'TRIP') {
        // Use charter number if it's a string, otherwise use trip ID
        const identifier = typeof entityId === 'string' && entityId.startsWith('CH-') 
          ? entityId 
          : entityId;
        endpoint = `/api/audit/charter/${identifier}`;
      } else if (entityType === 'sector' || entityType === 'SECTOR') {
        endpoint = `/api/audit/sector/${entityId}`;
      } else {
        return;
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Get most recent logs
      const logs = Array.isArray(response.data) ? response.data : (response.data.auditLogs || []);
      setAuditLogs(logs.slice(0, maxItems));
    } catch (error) {
      console.error('Failed to fetch recent audit logs:', error);
      // Global AxiosInterceptor will handle 403 errors with toast notifications
      // Other errors are logged but don't show toast to avoid noise
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatActorName = (actor) => {
    if (!actor) return 'System';
    if (typeof actor === 'string') return 'Unknown User';
    const name = `${actor.firstName || ''} ${actor.lastName || ''}`.trim();
    return name || actor.email || 'Unknown User';
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getActionColor = (action) => {
    if (action.includes('CREATED')) return 'success';
    if (action.includes('UPDATED')) return 'info';
    if (action.includes('DELETED') || action.includes('REJECTED')) return 'error';
    if (action.includes('APPROVED')) return 'success';
    return 'default';
  };

  const handleViewFull = () => {
    navigate('/audit', { 
      state: { 
        filters: { 
          entityType: entityType === 'charter' ? 'TRIP' : 'SECTOR',
          ...(entityType === 'charter' ? { charterNumber: entityName } : { sectorId: entityId })
        }
      }
    });
  };

  if (!entityId || auditLogs.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
              Recent Activity
            </Typography>
          </Box>
          <Box>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Tooltip title="View Full Audit Trail">
              <IconButton size="small" onClick={handleViewFull}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={expanded || auditLogs.length <= 3}>
          <List dense>
            {auditLogs.map((log, idx) => (
              <ListItem key={log._id || idx} sx={{ pl: 0, py: 0.5 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={log.action}
                        size="small"
                        color={getActionColor(log.action)}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(log.createdAt)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      By: {formatActorName(log.actorId)}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
          {auditLogs.length >= maxItems && (
            <Button
              size="small"
              onClick={handleViewFull}
              sx={{ mt: 1 }}
            >
              View All Activity
            </Button>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default QuickAuditTrail;

